<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
      :hasBack="false"
  >
    <template #subHeaderActions>
      <div class="d-flex align-items-center justify-content-end">
        <Input
            container-class="me-3"
            inputClass="form-control form-control-sm"
            name="input-search"
            :isInputGroup="true"
            :placeholder="$t('label.search')"
            v-model="query.search"
        >
          <template #rightAddon>
                    <span class="position-absolute border-0 bg-transparent end-0 top-50 translate-middle">
                      <i class="fa fa-search"></i>
                    </span>
          </template>
        </Input>
        <MonthPicker
            id="payslip-month-picker"
            :current-date="query.date"
            @update:date="handleDateChange" />
        <ButtonDropdownFilter
            :btn-title="$t('payslip.label.allStatus')"
            :search="false"
            :options="payslipStatus"
            value-field="value"
            :name-field="locale"
            v-model="query.status"
        />
      </div>
    </template>
    <template #layoutContent>
      <Loader :is-loading="isLoading">
        <div class="row mb-10">
          <div class="col-12">
            <Card class="card shadow-sm mb-5 p-5 px-10">
              <div class="d-flex justify-content-start align-items-center mt-2">
                <h3 class="mt-1"> {{ currentCompany?.name }} </h3>
                <span class="ms-5"> {{ $t('payslip.selectCompanyDesc') }} </span>
              </div>

              <div class="row mt-5 pt-5">
                <OfficeCountCard class="mb-4"  v-for="office in offices" attr="payslips_count" :key="office.id" :office="office" v-model="query.office"/>
              </div>
            </Card>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <Card :title="$t('payslip.list')">
              <template v-if="query.office" #cardToolbar>
                <div class="w-100 d-flex align-items-center justify-content-end">
                  <Button
                      v-if="canDownloadMany && $can(abilities.DOWNLOAD_PAYSLIP)"
                      type="new"
                      btn-class="btn-sm mx-2"
                      btn-text="payslip.downloads"
                      @click.prevent="() => downloadPayslips()"
                      :is-loading="isDownloading"
                  />
                  <Button
                      v-if="canGenerateExpense && $can(abilities.GENERATE_PAYSLIP)"
                      type="new"
                      btn-class="btn-sm mx-2"
                      btn-text="payslip.generateExpense"
                      @click.prevent="generatePayslipExpense"
                      :is-loading="isGenerating"
                  />
                  <Button
                      v-if="hasManyPayslipChecked && $can(abilities.VALIDATE_PAYSLIP)"
                      type="new"
                      btn-class="btn-sm mx-2"
                      btn-text="payslip.validates"
                      @click.prevent="() => validatePayslips()" />
                  <Button
                      v-if="canCreatePayslip && $can(abilities.CREATE_PAYSLIP)"
                      type="new"
                      btn-class="btn-sm mx-3"
                      btn-text="payslip.create"
                      @click.prevent="createNewPayslip" />
                </div>
              </template>
              <template #cardBody>
                <div v-if="!query.office">
                  <div class="h-350px d-flex justify-content-center align-items-center" >
                    <div class=" text-center ">
                      <div class="pt-8 ">
                        <Img filename="empty.png" alt=""
                             class="mw-100 h-125px h-sm-175px" />
                      </div>
                      <h2 class="fs-2x fw-bolder mb-0">{{ $t('payslip.noData') }}</h2>
                      <p class="mt-3 fs-4"> {{ $t('payslip.noOfficeSelected') }} </p>
                    </div>
                  </div>
                </div>
                <div v-else-if="isFetching">
                  <div class="h-350px d-flex justify-content-center align-items-center" >
                    <div class="text-center">
                      <div class="spinner-border" role="status">
                        <span class="visually-hidden">{{ $t("loadingText") }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else-if="payslips.length" id="scrollable-list-container" ref="table" class="table-container border">
                  <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4">
                    <thead class="bg-gray-100">
                    <tr>
                      <th class="w-50px fw-bold fs-5 py-5">
                        <div v-if="canValidateMany" class="form-check">
                          <input v-model="hasManyPayslipChecked" class="form-check-input form-check-sm" type="checkbox" id="flexCheckDefault" @change.prevent="toggleAllPayCheck">
                        </div>
                      </th>
                      <th class="col fw-bold fs-5 py-5">{{ $t('payslip.table.employee') }}</th>
                      <th class="col fw-bold fs-5 py-5">{{ $t('payslip.table.netToPay') }}</th>
                      <th class="col fw-bold fs-5 py-5">{{ $t('payslip.table.status') }} </th>
                      <th class="col fw-bold fs-5 py-5">{{ $t('label.action') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="payslip in payslips" :key="payslip.id">
                      <td class="w-50px fs-5 py-5">
                        <div v-if="canValidateMany" class="form-check">
                          <input class="form-check-input form-check-sm" type="checkbox" v-model="payslipsChecked[payslip.id]" id="flexCheckDefault">
                        </div>
                      </td>
                      <td class="col-4 fs-5 py-5">
                        {{ payslip?.user?.fullname }}
                      </td>
                      <td class="col fs-5 py-5">
                        {{ $formatLocalAmount(payslip?.paid_salary) }} {{ payslip?.currency_symbol }}
                      </td>
                      <td class="col fs-5 py-5">
                        <span class="badge" :class="'badge-light-'+payslip?.status?.color"> {{ payslip?.status?.[locale] }} </span>
                      </td>
                      <td class="col fs-5 py-5">
                        <div class="d-flex">
                          <router-link :to="{ name: 'payslip-file', params: { id: payslip.id } }">
                            <a href="#" class="btn btn-sm btn-clean btn-icon" :title="$t('button.download')"> <i class="fas fa-eye fs-2"></i> </a>
                          </router-link>
                          <a v-if="(payslipEnums.VALIDATED === payslip?.status?.value) && $can(abilities.DOWNLOAD_PAYSLIP)" href="#" class="btn btn-sm btn-clean btn-icon" :title="$t('button.download')" @click.prevent="() => downloadPayslips(payslip?.id)"> <i class="fas fa-download fs-2"></i> </a>
                          <a v-if="(payslipEnums.UNVALIDATED === payslip?.status?.value) && $can(abilities.UPDATE_PAYSLIP)" href="#" class="btn btn-sm btn-clean btn-icon" :title="$t('button.edit')" @click.prevent="() => openEditModal(payslip)"> <i class="fas fa-edit fs-2"></i> </a>
                          <a v-if="(payslipEnums.UNVALIDATED === payslip?.status?.value) && $can(abilities.VALIDATE_PAYSLIP)" href="#" class="btn btn-sm btn-clean btn-icon" :title="$t('button.validate')" @click.prevent="() => validatePayslips(payslip?.id)">
                            <i class="fas fa-check fs-2"></i>
                          </a>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else>
                  <div class="h-350px d-flex justify-content-center align-items-center" >
                    <div class=" text-center ">
                      <div class="pt-8 ">
                        <Img filename="empty.png" alt=""
                             class="mw-100 h-125px h-sm-175px" />
                      </div>
                      <h2 class="fs-2x fw-bolder mb-0">{{ $t('payslip.noData') }}</h2>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </div>
      </Loader>
      <UpdatePayslipModal
          id="update-payslip-modal"
          :user="editFormData.user"
          :currency="editFormData.currency"
          :payslip="editFormData?.id"
          @afterCreate="() => fetchOfficePayslips(true)"
      />
    </template>
  </SubLayout>
</template>
<script setup>

import Card from "@/components/Card.vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useRoute, useRouter} from "vue-router";
import {useStore} from "vuex";
import {useLocales} from "@/composables/locales";
import {useSwal} from "@/composables/useSwal";
import {computed, ref, watch, reactive, onMounted} from "vue";
import Loader from "@/components/Loader.vue";
import Button from "@/components/Button.vue";
import Input from "@/components/Input.vue";
import {$formatLocalAmount, getMonthLocalName} from "@/mixins/Helpers"; 
import MonthPicker from "@/components/MonthPicker.vue";
import {usePayslipEnums} from "@/Modules/RH/composables/payslipEnums";
import i18n from "@/plugins/i18n";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
import UpdatePayslipModal from "@/Modules/RH/components/Modals/Payslip/UpdatePayslipModal.vue";
import {getPayslipStatus} from "@/Modules/RH/store/modules/payslip/getters";
import abilities from "@/composables/abilities";
import Img from "@/components/Img.vue";
import OfficeCountCard from "@/components/OfficeCountCard.vue";

const route = useRoute()
const router = useRouter()
const store = useStore()
const {locale, datatableLocaleUrl} = useLocales()
const {showSwal} = useSwal()
const { payslipEnums } = usePayslipEnums()

const title = ref("breadcrumbs.payslip.title")
const breadcrumbs = ref([
  {
    name: 'breadcrumbs.payslip.list',
    route: 'payslips'
  }
])
const table = ref()
const isLoading = ref(true)
const isValidating = ref(false)
const isDownloading = ref(false)
const isFetching = ref(false)
const isGenerating = ref(false)
const query = ref({
  status: '',
  type: '',
  search: '',
  office: '',
  date: new Date(),
  year: new Date().getFullYear(),
  month: String(new Date().getMonth() + 1).padStart(2, '0')
})
const payslips = ref([])
const payslipsMetaData = reactive({
  current_page: 1,
  last_page: 1,
  next_page: null
})
const payslipsChecked = ref({})
const hasManyPayslipChecked = ref(false)
const editFormData = ref({
  payslip_id: "",
  office_id: "",
  user_id: "",
  month: "",
  year: "",
  base_salary: "",
  allowances: [{
    id: "",
    value: 0,
    edited: false
  }],
  others: [{
    id: "",
    value: 0,
    edited: false
  }],
  pensions: [{
    id: "",
    value: 1,
    edited: false
  }],
  taxes: [{
    id: "",
    value: 1,
    edited: false
  }],
  pension_employers: [{
    id: "",
    value: 1,
    edited: false
  }],
  taxe_employers: [{
    id: "",
    value: 1,
    edited: false
  }],
  user: {},
  currency: ''
})
const canDownloadMany = ref(false)
const canGenerateExpense = ref(false)
const canValidateMany = ref(false)
const canCreatePayslip = ref(false)


const payslipStatus = computed(() => store.getters['payslip/getPayslipStatus'])
const offices = computed(() => store.getters['payslip/payslipCompanyOffices'])
const currentCompany = computed(() => store.getters['offices/getSelectedCompany'])


watch(() => currentCompany.value?.id, () => {
  getPayslipCompanyOffices()
})

watch(() => query.value, (value) => {
  router.replace({
    name: route.name,
    params: { ...route.params },
    query: {
      office: query.value.office,
      month: query.value.month,
      year: query.value.year,
      status: query.value.status,
    }
  })
  fetchOfficePayslips(true)
}, { deep: true })



const handleDateChange = (date) => {
  query.value.date = date.date
  query.value.year = date.year
  query.value.month = date.month
  router.replace({
    name: route.name,
    params: { ...route.params },
    query: {...route.query, year: date.year, month: date.month},
  })
}

const createNewPayslip = () => {
  if(query.value.office) {
    router.push({
      name: 'payslips-office-create',
      params: { office: query.value.office },
      query: {
        month: query.value.month,
        year: query.value.year,
      }
    })
  }
}

function downloadPayslips (id) {
  let data = id
      ? { office_id: '', payslip: id, year: query.value.year, month: query.value.month }
      : { office_id: query.value.office, payslip: '', year: query.value.year, month: query.value.month }

  isDownloading.value = true
  store.dispatch('payslip/downloadPayslips', data)
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
        const link = document.createElement('a');
        let tmp = id
            ? payslips.value.find(payslip => payslip.id === id)?.user.fullname.replace(' ', '-')
            : offices.value.find(office => office.id === query.value.office)?.name.replace(' ', '-');
        link.href = url;
        link.setAttribute(
            'download',
            `${i18n.global.t('payslip.filename')}-${tmp}-${getMonthLocalName(query.value.month,query.value.year, locale.value)}-${query.value.year}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .finally(() => isDownloading.value = false)
}

const validatePayslips = (id) => {
  let data = id ? [id] : Object.keys(payslipsChecked.value)
  let notif = id ? 'payslip.notification.payslip_validated' : 'payslip.notification.payslips_validated'
  let swal = id ? 'payslip.swal.validate_text' : 'payslip.swal.validates_text'

  showSwal(
      i18n.global.t('payslip.swal.validated'),
      i18n.global.t(swal)
  ).then( async (result) => {
    if (result.value) {
      isValidating.value = true
      store.dispatch('payslip/validatePayslips', { payslips: data })
          .then(res => {
            window.toastr['success'](i18n.global.t(notif))
            fetchOfficePayslips(true)
          })
          .catch(() => {

          })
          .finally(() => isValidating.value = false)
    }
  })
}

const generatePayslipExpense = () => {
  showSwal(
      i18n.global.t('payslip.swal.generate'),
      i18n.global.t('payslip.swal.generate_text')
  ).then( async (result) => {
    if (result.value) {
      isGenerating.value = true
      const data = {
        office_id: query.value.office,
        year: query.value.year,
        month: query.value.month
      }
      store.dispatch('payslip/generatePayslipExpense', data)
          .then(res => {
            window.toastr['success'](i18n.global.t('payslip.notification.generated'))
            fetchOfficePayslips(true)
          })
          .catch(() => {

          })
          .finally(() => isGenerating.value = false)
    }
  })
}

const toggleAllPayCheck = (e) => {
  payslips.value.forEach(el => {
    payslipsChecked.value[el.id] = e.target.checked
  })
}

const fetchOfficePayslips = (restart = false) => {
  editFormData.value = null
  editFormData.value = {}
  isFetching.value = true
  store.dispatch('payslip/getPayslipOfficeEmployees', {
    year: query.value.year,
    month: query.value.month,
    office_id: query.value.office,
    status: query.value.status
  })
      .then(res => {
        if(!payslips.value.length || restart) {
          payslips.value = []
          payslips.value = res.data.payslips
          canDownloadMany.value = res.data.can_download
          canValidateMany.value = res.data.payslips_unvalidated
          canCreatePayslip.value = res.data.can_create_payslip
          canGenerateExpense.value = res.data.can_generate_expense
          return;
        }
        payslips.value = payslips.value.concat(res.data.payslips)
      })
      .finally(() => {
        isFetching.value = false
        if (!table.value) {
          loadNextPage()
        }
      })
}

const loadNextPage = () => {
  table.value = document.getElementById('scrollable-list-container');
  if(table.value) {
    table.value.addEventListener('scroll', () => {
      if (table.value.scrollTop + table.value.clientHeight >= table.value.scrollHeight - 100) {
        if (!isFetching.value) {
          if(payslipsMetaData.current_page < payslipsMetaData.last_page){
            fetchOfficePayslips(false, payslipsMetaData.current_page++);
          }
        }
      }
    });
  }
};

const openEditModal = (payslip) => {
  editFormData.value.id = payslip.id

  $('#update-payslip-modal').modal('toggle')
}

function getPayslipCompanyOffices() {
  if(currentCompany.value?.id) {
    isLoading.value = true
    store.dispatch('payslip/getPayslipCompanyOffices', {
      year:  query.value.year,
      month: query.value.month,
      society_id: currentCompany.value?.id,
    })
        .then(res => {
          query.value.office = res.data.data[0]?.id
          router.replace({
            name: route.name,
            query: {
              office: query.value.office,
              month: query.value.month,
              year: query.value.year,
              status: query.value.status,
            }
          })
        })
        .catch()
        .finally(() => isLoading.value = false)
  }
}

onMounted(() => {
  store.dispatch('payslip/getPayslipStatus')
  if(route.query.office) {
    query.value.office = route.query.office
  }
  if(route.query.year && route.query.month) {
    query.value.year = route.query.year
    query.value.month = route.query.month
    query.value.date = new Date(`${route.query.year}-${route.query.month}-01`)
  }
  if(route.query.status) {
    query.value.status = route.query.status
  }
  getPayslipCompanyOffices()
})

</script>


<style scoped>
.table-container {
  height: 350px;
  overflow-y: auto;
}
thead th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 1;
}
</style>